import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, CheckCheck } from 'lucide-react'

import { communicationService, type Notification } from '../services/communicationService'
import { ClientRegister, StatusDot, type RegisterColumn } from './register/ClientRegister'
import { DetailsModal } from './register/DetailsModal'
import { notificationConfig, notificationKind, notificationLink, notificationTone } from './communication/notificationRegister'

const when = (value?: string | null) => (value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '')

const readState = (notification: Notification) =>
  notification.read_at ? { label: 'Read', dot: 'bg-gray-300' } : { label: 'Unread', dot: 'bg-primary-600' }

const columns: RegisterColumn<Notification>[] = [
  { header: 'Status', cell: (item) => <StatusDot {...readState(item)} />, exportValue: (item) => readState(item).label },
  { header: 'About', cell: (item) => <StatusDot label={notificationKind(item)} dot={notificationTone(item)} />, exportValue: notificationKind },
  {
    header: 'Title',
    cell: (item) => <span className={item.read_at ? 'text-gray-900' : 'font-semibold text-gray-900'}>{item.title}</span>,
    exportValue: (item) => item.title,
    className: 'max-w-[18rem] truncate',
  },
  { header: 'Details', cell: (item) => item.message, exportValue: (item) => item.message, className: 'max-w-[24rem] truncate text-gray-500' },
  { header: 'Received', cell: (item) => when(item.created_at), exportValue: (item) => when(item.created_at), className: 'tabular-nums text-gray-600' },
]

/** Alerts addressed to the signed-in person. Every role has these, whatever else it may manage. */
export default function MyNotifications() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState<Notification | null>(null)
  const notifications = useQuery({ queryKey: ['communication', 'notifications'], queryFn: () => communicationService.getNotifications() })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['communication', 'notifications'] })
    // The bell in the header counts these too.
    void queryClient.invalidateQueries({ queryKey: ['communication', 'unread-counts'] })
  }
  const readOne = useMutation({ mutationFn: communicationService.markNotificationAsRead, onSuccess: refresh })
  const readAll = useMutation({ mutationFn: () => communicationService.markAllNotificationsAsRead(), onSuccess: refresh })
  const unread = notifications.data?.results.filter((item) => !item.read_at).length ?? 0

  const show = (item: Notification) => {
    setOpen(item)
    if (!item.read_at) readOne.mutate(item.id)
  }
  const link = open ? notificationLink(open.url) : null

  return (
    <>
      <ClientRegister
        title="Your notifications"
        description="Click one to read it in full and go straight to what it is about."
        actions={unread > 0 ? (
          <button type="button" onClick={() => readAll.mutate()} disabled={readAll.isPending} className="pager-button h-8">
            <CheckCheck className="h-3.5 w-3.5" />Mark {unread} as read
          </button>
        ) : undefined}
        rows={notifications.data?.results}
        config={notificationConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={show}
        openLabel={() => 'Read'}
        noun={['notification', 'notifications']}
        exportName={{ file: 'notifications', title: 'Notifications' }}
        isLoading={notifications.isLoading}
        isError={notifications.isError}
        onRetry={() => void notifications.refetch()}
        emptyText="You’re all caught up. New alerts and updates will appear here."
      />

      {open && (
        <DetailsModal
          title={open.title}
          headline={{ label: notificationKind(open), value: open.title, note: when(open.created_at) }}
          status={{ label: notificationKind(open), dot: notificationTone(open) }}
          facts={[
            ['Received', when(open.created_at)],
            ['Read', when(open.read_at) || 'Just now'],
          ]}
          sections={[{ title: 'Details', body: open.message }]}
          onClose={() => setOpen(null)}
          footer={
            <>
              <button type="button" onClick={() => setOpen(null)} className="btn-outline">Close</button>
              {link && (
                <button type="button" onClick={() => { setOpen(null); navigate(link.to) }} className="btn btn-primary">
                  {link.label}<ArrowRight className="ml-2 h-4 w-4" />
                </button>
              )}
            </>
          }
        />
      )}
    </>
  )
}
